import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Row, Col, Card, Table, Form, Button, Spinner, Alert, Pagination } from 'react-bootstrap';
import { useCatalogue } from '../hooks/useCatalogue';
import AppIcon from './AppIcon';
import AddStockToPortfolioModal from './AddStockToPortfolioModal';

interface Stock {
  id: number;
  symbol: string;
  name: string;
  isin: string;
}

const Stocks: React.FC = () => {
  const { items: stocks, loading, error, searchTerm, setSearchTerm, currentPage, totalPages, totalCount, search, changePage, retry } = useCatalogue<Stock>('stocks');
  const [stockToAdd, setStockToAdd] = useState<Stock | null>(null);
  const [addSuccess, setAddSuccess] = useState('');

  const handleSearch = (event: React.FormEvent) => { event.preventDefault(); search(); };
  const handlePageChange = changePage;

  const handleAddToPortfolio = (stock: Stock) => {
    setAddSuccess('');
    setStockToAdd(stock);
  };

  return (
    <div className="directory-page">
      <header className="directory-hero">
        <div className="directory-hero__icon"><AppIcon name="stocks" /></div>
        <div>
          <span className="page-eyebrow">Market catalogue</span>
          <h1>Stocks</h1>
          <p>Find exchange-listed companies, review market details, and add the right instrument to your portfolio.</p>
        </div>
      </header>

      {addSuccess && (
        <Alert variant="success" dismissible onClose={() => setAddSuccess('')}>
          {addSuccess}
        </Alert>
      )}

      {error && <Alert variant="warning">{error} <Button size="sm" variant="outline-dark" onClick={retry}>Retry</Button></Alert>}
      {/* Search */}
      <Card className="directory-search-card mb-4">
        <Card.Body>
          <p className="workspace-status">Results update as you type. Search by name or identifier.</p>
          <Form onSubmit={handleSearch}>
            <Row className="align-items-end g-3">
              <Col md={8}>
                <Form.Group controlId="stocks-search">
                  <Form.Label>Search the stock catalogue</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Search by symbol, name or ISIN..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={4} className="d-grid">
                <Button type="submit" variant="primary" className="directory-search-button" disabled={loading}>
                  {loading ? 'Searching…' : 'Search now'}
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      {/* Results */}
      <Card className="directory-results-card">
        <Card.Header className="directory-results-card__header">
          <div><span className="page-eyebrow">Search results</span><h2>Available stocks</h2></div>
          {!loading && <span className="directory-results-card__count">{totalCount} results</span>}
        </Card.Header>
        <Card.Body className="p-0">
          {loading && stocks.length === 0 ? (
            <div className="d-flex justify-content-center p-4">
              <Spinner animation="border" />
            </div>
          ) : stocks.length > 0 ? (
            <>
              <Table aria-busy={loading} responsive hover className="directory-table mb-0">
                <thead>
                  <tr>
                    <th>Symbol</th>
                    <th>Name</th>
                    <th>ISIN</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {stocks.map((stock) => (
                    <tr key={stock.id}>
                      <td className="directory-table__symbol">
                        <strong>
                          <Link to={`/stocks/${stock.id}`}>
                            {stock.symbol}
                          </Link>
                        </strong>
                      </td>
                      <td className="directory-table__name">
                        <Link to={`/stocks/${stock.id}`}>
                          {stock.name}
                        </Link>
                      </td>
                      <td>
                        <code>{stock.isin || 'N/A'}</code>
                      </td>
                      <td>
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => handleAddToPortfolio(stock)}
                        >
                          Add to Portfolio
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="d-flex justify-content-center p-3">
                  <Pagination>
                    <Pagination.Prev
                      disabled={currentPage === 1}
                      onClick={() => handlePageChange(currentPage - 1)}
                    />

                    {[...Array(Math.min(5, totalPages))].map((_, index) => {
                      const page = currentPage - 2 + index;
                      if (page < 1 || page > totalPages) return null;

                      return (
                        <Pagination.Item
                          key={page}
                          active={page === currentPage}
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </Pagination.Item>
                      );
                    })}

                    <Pagination.Next
                      disabled={currentPage === totalPages}
                      onClick={() => handlePageChange(currentPage + 1)}
                    />
                  </Pagination>
                </div>
              )}
            </>
          ) : (
            <div className="text-center p-4 text-muted">
              {searchTerm ? 'No stocks found matching your search.' : 'No stocks available.'}
            </div>
          )}
        </Card.Body>
      </Card>

      <AddStockToPortfolioModal
        show={Boolean(stockToAdd)}
        stock={stockToAdd}
        onHide={() => setStockToAdd(null)}
        onSuccess={(addedStock) => setAddSuccess(`${addedStock.symbol} was added to your portfolio.`)}
      />
    </div>
  );
};

export default Stocks;
